import { useQuery } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthProvider';
import type { OrgSummary, Pricing, Role } from '../lib/types';
import { qk } from '../queries/keys';
import { fetchOrgs } from '../queries/orgs';

/**
 * The organization the person is in, if any.
 *
 * A person is in one at a time, and while they are, their library is the
 * organization's and the organization's is theirs. There is nothing to pick:
 * the API stamps every recording with the organization it was made in, and
 * lists what the person owns alongside what the organization holds. This
 * only says which one that is, and what the person may do in it.
 */

export interface OrgContextValue {
  /** Null outside any organization. */
  org: OrgSummary | null;
  role: Role | null;
  /** Seat price and trial length, once loaded. */
  pricing: Pricing | null;
  loading: boolean;
  error: unknown;
  refetch: () => void;
}

const OrgContext = createContext<OrgContextValue | null>(null);

export function OrgProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: qk.orgs(),
    queryFn: fetchOrgs,
    enabled: Boolean(user),
    staleTime: 60_000,
  });

  // The API answers with a list for the shape it always had; it holds one.
  const org = query.data?.orgs[0] ?? null;
  const pricing = query.data?.pricing ?? null;

  const refetch = useCallback(() => {
    void query.refetch();
  }, [query]);

  const value = useMemo<OrgContextValue>(
    () => ({
      org,
      role: org?.role ?? null,
      pricing,
      loading: query.isLoading,
      error: query.error,
      refetch,
    }),
    [org, pricing, query.error, query.isLoading, refetch],
  );

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgContextValue {
  const value = useContext(OrgContext);
  if (!value) throw new Error('useOrg must be used inside <OrgProvider>.');
  return value;
}
